import { useLayoutEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { useConsent } from '../../context';
import { useT } from '../../i18n/LocaleContext';

/**
 * The cookie question as one slim bar along the bottom of the window: a line and
 * three choices, over nothing that matters. The card it replaced sat in the middle
 * of the bottom edge, over the Pro price on /pricing and the idea box on the home
 * page, and over the bottom quarter of a phone. Analytics stays off until "Allow".
 *
 * While it shows, its height is published as --consent-bar-h on <html>, so anything
 * else pinned to the bottom (a phone's Export bar) can sit above it.
 */
export function ConsentBanner() {
  const { analyticsConsent, grantAnalytics, denyAnalytics, openPreferences, closePreferences } = useConsent();
  const t = useT();
  const ref = useRef(null);
  const shown = analyticsConsent === 'unknown';

  useLayoutEffect(() => {
    if (!shown) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--consent-bar-h', `${ref.current?.offsetHeight || 0}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    if (observer && ref.current) observer.observe(ref.current);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--consent-bar-h');
    };
  }, [shown]);

  if (!shown) return null;

  const decide = (choose) => () => {
    choose();
    closePreferences();
  };

  return (
    // data-prerender-skip: a visitor's choice, not the page. Left in the prerendered
    // HTML it was the first heading of every page Google read (scripts/prerender.mjs)
    <div
      ref={ref}
      role="region"
      aria-label={t('consent.title')}
      data-prerender-skip
      data-testid="consent-bar"
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-card/95 px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <p className="flex-1 text-xs text-muted-foreground sm:text-sm">
          <span className="text-foreground">{t('consent.title')}.</span>{' '}
          {t('consent.learnMore')} <RouterLink to="/privacy" className="underline">{t('consent.policy')}</RouterLink>.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={openPreferences} className="h-8 text-xs">{t('consent.manage')}</Button>
          <Button size="sm" variant="outline" onClick={decide(denyAnalytics)} className="h-8 text-xs">{t('consent.decline')}</Button>
          <Button size="sm" onClick={decide(grantAnalytics)} className="h-8 text-xs">{t('consent.allow')}</Button>
        </div>
      </div>
    </div>
  );
}
